'use client';

import { TooltipComponent } from '@/src/components/layout';
import { Icon, Spinner, useToast } from '@/src/components/ui';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Button } from '@workspace/ui/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { Checkbox } from '@workspace/ui/components/checkbox';
import { useState } from 'react';
import { reportBit } from '@/src/actions/bits';
import Link from 'next/link';
import { useProfile } from '@/src/hooks/use-profile';
import { reportComment } from '@/src/actions/comments';
import { cn } from '@workspace/ui/lib/utils';
import { reportProject } from '@/src/actions/projects';
import { reportReview } from '@/src/actions/reviews';
import { reportSocialPost } from '@/src/actions/social-posts';
import { reportSocialComment } from '@/src/actions/social-comments';

type Props = {
  bitId?: number;
  commentId?: number;
  projectId?: number;
  reviewId?: number;
  socialPostId?: number;
  socialCommentId?: number;
  type?: 'bit' | 'comment' | 'project' | 'review' | 'post' | 'postComment';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

const REPORT_REASONS = [
  'Hate speech or harassment',
  'Pornographic or sexually explicit material',
  'Malware, viruses, or harmful scripts',
  'Illegal or infringing material',
  'Misleading or promotional content',
  'Low-effort content',
  'GenAI',
  'Copyright',
];

export const Report = ({
  bitId,
  commentId,
  reviewId,
  type,
  projectId,
  open,
  socialPostId,
  socialCommentId,
  onOpenChange,
  hideTrigger = false,
}: Props) => {
  const isLoading = useBoolean(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const toast = useToast();
  const { data: profile } = useProfile();
  const isAnonymous = profile?.type === 'anonymous';
  const isComment = type === 'comment';
  const isReview = type === 'review';
  const isProject = type === 'project';
  const isPost = type === 'post';
  const isPostComment = type === 'postComment';

  const internal = useBoolean(false);
  const isControlled = typeof open === 'boolean';
  const openValue = isControlled ? !!open : internal.value;
  const setOpen = (v: boolean) => {
    if (isControlled) onOpenChange?.(v);
    else v ? internal.onTrue() : internal.onFalse();
  };

  const toggleReason = (reason: string) => {
    setSelectedReasons((prev) =>
      prev.includes(reason)
        ? prev.filter((r) => r !== reason)
        : [...prev, reason]
    );
  };

  const resetState = () => setSelectedReasons([]);
  const handleClose = () => {
    resetState();
    setOpen(false);
  };

  const handleOpenChange = (o: boolean) => {
    if (!o) handleClose();
    else setOpen(true);
  };

  const handleReport = async () => {
    if (!selectedReasons.length) return;
    isLoading.onTrue();
    try {
      let data: any, error: any;

      if (isComment && commentId && bitId) {
        ({ data, error } = await reportComment(
          bitId,
          commentId,
          selectedReasons.join(', '),
          ''
        ));
      } else if (isProject && projectId) {
        ({ data, error } = await reportProject(
          projectId,
          selectedReasons.join(', '),
          ''
        ));
      } else if (bitId) {
        ({ data, error } = await reportBit(
          bitId,
          selectedReasons.join(', '),
          ''
        ));
      } else if (isReview && reviewId && projectId) {
        ({ data, error } = await reportReview(
          projectId,
          reviewId,
          selectedReasons.join(', '),
          ''
        ));
      } else if (isPost && socialPostId) {
        ({ data, error } = await reportSocialPost(
          socialPostId,
          selectedReasons.join(', '),
          ''
        ));
      } else if (isPostComment && socialCommentId) {
        ({ data, error } = await reportSocialComment(
          socialCommentId,
          selectedReasons.join(', '),
          ''
        ));
      }

      if (error || !data) {
        toast.error(`Error reporting ${type}`);
        return;
      }
      toast.success(`${type} reported successfully`);
      handleClose();
    } finally {
      isLoading.onFalse();
    }
  };

  const labelType = type ?? 'item';
  const label = labelType.charAt(0).toUpperCase() + labelType.slice(1);

  return (
    <>
      {!hideTrigger && (
        <TooltipComponent
          text={
            isAnonymous
              ? `Log in to report a ${labelType}`
              : `Report this ${labelType}`
          }
          side="right"
        >
          <Button
            type="button"
            variant={isComment || isReview ? 'ghost' : 'outline'}
            size="icon"
            className={cn('group transition-all duration-200 h-8 w-8', {
              'border-0 bg-transparent size-fit text-red-error': projectId,
            })}
            onClick={!isAnonymous ? () => setOpen(true) : undefined}
          >
            <Icon
              name="report"
              className={cn('size-4 text-red-error', {
                'hover:text-red-error text-black/40 group-hover:text-red-error':
                  isComment || isReview,
              })}
            />
            {isProject ? 'Report' : null}
          </Button>
        </TooltipComponent>
      )}

      <Dialog
        open={openValue}
        onOpenChange={handleOpenChange}
        data-no-card-click="true"
      >
        <DialogContent className="gap-1">
          <DialogHeader className="mb-4">
            <DialogTitle className="mb-4 text-center">
              Would you like to report this {label}?
            </DialogTitle>
            <DialogDescription className="text-black">
              Please confirm that it violates one of the following according to
              our{' '}
              <Link
                href="/terms-and-conditions"
                target="_blank"
                className="hover:cursor-pointer hover:underline"
              >
                <b className="cursor-pointer">Terms & Conditions</b>
              </Link>
              :
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 px-1 py-2 text-sm">
            {REPORT_REASONS.map((reason) => (
              <label
                className="flex items-center gap-2"
                key={reason}
                data-no-card-click="true"
              >
                <Checkbox
                  data-no-card-click="true"
                  checked={selectedReasons.includes(reason)}
                  onCheckedChange={() => toggleReason(reason)}
                  className="flex items-center checked:bg-blue-primary"
                />
                {reason}
              </label>
            ))}
          </div>

          <DialogFooter className="flex md:justify-between mt-4">
            <Button
              data-no-card-click="true"
              size="sm"
              variant="secondary"
              className="font-medium size-sm px-4"
              onClick={handleReport}
              disabled={isLoading.value || selectedReasons.length === 0}
            >
              {isLoading.value ? <Spinner /> : `Report this ${label}`}
            </Button>
            <DialogClose asChild>
              <Button
                data-no-card-click="true"
                type="button"
                size="sm"
                variant="outline"
                className="bg-transparent font-medium size-sm px-4"
                onClick={handleClose}
              >
                Cancel
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
